import { fixed, trim } from '../../math/format';
import { feedRate, rpmFromSfm } from '../../math/cutting';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

const CUTTERS = [0.25, 0.375, 0.5, 0.625, 0.75, 1.0] as const;
const SFMS = [80, 100, 120, 150, 200, 250, 300] as const;

export const SPEEDS_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'speeds.rpm',
    topicId: 'speeds',
    skill: 'RPM from SFM and diameter',
    gen: (rng) => {
      const diameter = rng.pick(CUTTERS);
      const sfm = rng.pick(SFMS);
      const solved = rpmFromSfm(sfm, diameter);
      const correct = `${Math.round(solved.value)} RPM`;

      return {
        kind: 'mc',
        prompt: `${fixed(diameter, 3)}" cutter, ${sfm} SFM. What RPM?`,
        formula: 'RPM = (3.82 × SFM) ÷ D',
        why: `(3.82 × ${sfm}) ÷ ${trim(diameter, 3)} = ${Math.round(solved.value)} RPM.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${Math.round(solved.value / 2)} RPM`,
            trap: 'Half the answer — check whether you used the radius where the diameter belongs.',
          },
          {
            label: `${Math.round(solved.value * 2)} RPM`,
            trap: 'Double. You may have divided by half the diameter.',
          },
          {
            label: `${Math.round(3.82 * sfm * diameter)} RPM`,
            trap: 'You multiplied by the diameter. It sits on the bottom of the fraction — bigger cutter, fewer RPM.',
          },
        ]),
      };
    },
  },
  {
    id: 'speeds.feed',
    topicId: 'speeds',
    skill: 'Feed rate from RPM, flutes and chip load',
    gen: (rng) => {
      const rpm = rng.step(400, 3000, 100);
      const flutes = rng.pick([2, 3, 4, 4, 6]);
      const load = rng.pick([0.001, 0.0015, 0.002, 0.003, 0.004]);
      const solved = feedRate(rpm, flutes, load);
      const correct = `${fixed(solved.value, 1)} IPM`;

      return {
        kind: 'mc',
        prompt: `${rpm} RPM, ${flutes} flutes, ${fixed(load, 4)}" chip load. Feed rate?`,
        formula: 'IPM = RPM × flutes × chip load',
        why: `${rpm} × ${flutes} × ${fixed(load, 4)} = ${fixed(solved.value, 2)} IPM.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${fixed(rpm * load, 2)} IPM`,
            trap: 'You left the flute count out. Every tooth takes a bite each revolution.',
          },
          {
            label: `${fixed(solved.value / flutes / flutes, 2)} IPM`,
            trap: 'You divided by the flutes instead of multiplying. More flutes means more feed, not less.',
          },
          {
            label: `${fixed(solved.value * 10, 1)} IPM`,
            trap: 'Decimal point is one place out on the chip load.',
          },
        ]),
      };
    },
  },
  {
    id: 'speeds.diameterEffect',
    topicId: 'speeds',
    skill: 'How diameter changes RPM',
    gen: (rng) => {
      const factor = rng.pick([2, 3, 4]);
      const words: Record<number, string> = { 2: 'doubles', 3: 'triples', 4: 'quadruples' };
      const answers: Record<number, string> = { 2: 'Halves', 3: 'Drops to a third', 4: 'Drops to a quarter' };

      return {
        kind: 'mc',
        prompt: `Same SFM, but the cutter diameter ${words[factor]}. RPM...`,
        formula: 'RPM = (3.82 × SFM) ÷ D',
        why: `Diameter is on the bottom of the fraction. ${factor} times the diameter means one ${factor === 2 ? 'half' : factor === 3 ? 'third' : 'quarter'} the RPM.`,
        steps: [
          { kind: 'formula', text: 'RPM = (3.82 × SFM) ÷ D' },
          { kind: 'note', text: 'D is a divisor. Anything you do to a divisor does the opposite to the answer.' },
          { kind: 'result', text: `${factor}× the diameter gives 1/${factor} the RPM.` },
        ],
        ...mcOptions(rng, answers[factor]!, [
          {
            label: words[factor]![0]!.toUpperCase() + words[factor]!.slice(1),
            trap: 'That is the wrong direction. Diameter is a divisor, so growing it shrinks the RPM.',
          },
          {
            label: 'Stays the same',
            trap: 'SFM staying the same is exactly what forces RPM to change — the cutter has further to travel each turn.',
          },
          {
            label: `Drops to a ${({ 2: 'quarter', 3: 'ninth', 4: 'sixteenth' } as Record<number, string>)[factor]}`,
            trap: 'You squared it. The relationship is straight division, not a square.',
          },
        ]),
      };
    },
  },
  {
    id: 'speeds.chipDiagnosis',
    topicId: 'speeds',
    skill: 'Reading a chip',
    gen: (rng) => {
      const cases = [
        {
          symptom: 'Chips are coming off burnt and thin.',
          answer: 'More feed per tooth',
          why: 'Thin burnt chips mean the tool is rubbing instead of cutting. Raising chip load gives it something to actually bite.',
          traps: [
            { label: 'More RPM', trap: 'That makes it worse — faster rubbing is still rubbing, and now it is hotter.' },
            { label: 'Less coolant', trap: 'Coolant is not what is burning it. The tool is not taking a real chip.' },
            { label: 'A shorter cutter', trap: 'Stickout affects chatter, not chip thickness.' },
          ],
        },
        {
          symptom: 'The cutter is chattering badly in a deep slot.',
          answer: 'Less stickout, or a smaller depth of cut',
          why: 'Chatter is a rigidity problem. Shorten the tool or take less at a time before you touch speeds.',
          traps: [
            { label: 'More RPM', trap: 'Sometimes it helps, but the first move on chatter is always rigidity.' },
            { label: 'A slower feed', trap: 'Backing the feed off often makes chatter worse by thinning the chip.' },
            { label: 'More coolant', trap: 'Coolant will not stiffen the setup.' },
          ],
        },
        {
          symptom: 'The finish is fine but the tool is wearing out fast.',
          answer: 'Lower the SFM',
          why: 'Rapid wear with a good finish usually means the surface speed is too high for the material.',
          traps: [
            { label: 'Raise the SFM', trap: 'Heat is what kills the edge, and SFM is the biggest heat lever you have.' },
            { label: 'Lower the chip load', trap: 'Thinner chips carry less heat away and wear the tool faster.' },
            { label: 'More flutes', trap: 'More flutes means more heat in the same cut, not less.' },
          ],
        },
      ] as const;

      const c = rng.pick(cases);
      return {
        kind: 'mc',
        prompt: c.symptom,
        sub: 'Most likely first fix?',
        why: c.why,
        steps: [
          { kind: 'note', text: c.symptom },
          { kind: 'result', text: c.why },
        ],
        ...mcOptions(rng, c.answer, [...c.traps]),
      };
    },
  },
];
